import React, { useEffect, useState } from 'react';
import './HouseBuyPrompt.scss';

interface ShowEventDetail {
  refrId: number;
  price: number;
  rentOnly?: boolean;
  rentPricePerWeek: number | null;
  holdName: string | null;
  // A commerce: its original furniture stays, so no "move in empty" choice
  commerce?: boolean;
  // Picked in the ALT menu: only that way is offered in the window
  choice?: 'buy' | 'rent' | null;
  // A sale offered by another player: accept or refuse
  offer?: boolean;
  sellerName?: string;
  houseName?: string;
  expiresInMs?: number;
}

const send = (payload: Record<string, unknown>): void => {
  if ((window as any).skyrimPlatform?.sendMessage) {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'house-buy-prompt', ...payload }));
  }
};

const HouseBuyPrompt: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [refrId, setRefrId] = useState<number | null>(null);
  const [price, setPrice] = useState(0);
  const [rentOnly, setRentOnly] = useState(false);
  const [rentPricePerWeek, setRentPricePerWeek] = useState<number | null>(null);
  const [holdName, setHoldName] = useState<string | null>(null);
  const [commerce, setCommerce] = useState(false);
  const [choice, setChoice] = useState<'buy' | 'rent' | null>(null);
  const [offer, setOffer] = useState<{ sellerName: string; houseName: string } | null>(null);
  // Move in with the original contents already taken out
  const [startEmpty, setStartEmpty] = useState(false);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      if (detail.offer) {
        setOffer({ sellerName: detail.sellerName ?? 'Un joueur', houseName: detail.houseName ?? '' });
        setPrice(detail.price);
        setRefrId(null);
        setChoice(null);
        setVisible(true);
        return;
      }
      setOffer(null);
      setChoice(detail.choice ?? null);
      setStartEmpty(false);
      setRefrId(detail.refrId);
      setPrice(detail.price);
      setRentOnly(detail.rentOnly === true);
      setRentPricePerWeek(detail.rentPricePerWeek ?? null);
      setHoldName(detail.holdName ?? null);
      setCommerce(detail.commerce === true);
      setVisible(true);
    };
    window.addEventListener('skymp5-client:promptHouseBuy', handler);
    return () => window.removeEventListener('skymp5-client:promptHouseBuy', handler);
  }, []);

  const close = () => {
    setVisible(false);
    send({ action: 'cancel' });
  };

  const answerOffer = (accept: boolean) => {
    send({ action: 'offerAnswer', accept });
    setVisible(false);
  };

  // "type" is new - defaults to 'buy' server-side when omitted, so this
  // stays compatible with the pre-rent server/client if either side hasn't
  // been updated together.
  const confirm = (type: 'buy' | 'rent') => {
    if (refrId === null) return;
    send({ action: 'confirm', refrId, type, startEmpty: commerce ? false : startEmpty });
    setVisible(false);
  };

  if (!visible) return null;

  if (offer) {
    return (
      <div className="house-buy">
        <div className="house-buy__window skyrim-panel">
          <div className="house-buy__title">Offre de logement</div>
          <div className="house-buy__hold">
            {offer.sellerName} te propose {offer.houseName ? `"${offer.houseName}"` : 'son logement'}.
          </div>
          <div className="house-buy__price">{price} Septims</div>
          <div className="house-buy__actions">
            <button type="button" className="house-buy__button house-buy__button--secondary" onClick={() => answerOffer(false)}>
              Refuser
            </button>
            <button type="button" className="house-buy__button" onClick={() => answerOffer(true)}>
              Accepter
            </button>
          </div>
        </div>
      </div>
    );
  }

  // What the window offers: both ways, or only the one picked in the ALT menu
  const canRent = rentPricePerWeek !== null && rentPricePerWeek > 0 && choice !== 'buy';
  const canBuy = !rentOnly && choice !== 'rent';

  return (
    <div className="house-buy">
      <div className="house-buy__window skyrim-panel">
        <div className="house-buy__title">
          {!canBuy ? (commerce ? 'Louer ce commerce ?' : 'Louer cette maison ?') : holdName ? `Demander cette maison ?` : 'Acheter cette maison ?'}
        </div>
        {canBuy && <div className="house-buy__price">{price} Septims</div>}
        {canRent && (
          <div className="house-buy__rent-price">{canBuy ? 'ou ' : ''}{rentPricePerWeek} Septims/semaine en location</div>
        )}
        {holdName && canBuy && <div className="house-buy__hold">Approbation de l'Intendant de {holdName} requise pour l'achat.</div>}
        {!commerce && !(holdName && canBuy && !canRent) && (
          <label className="house-buy__empty">
            <input type="checkbox" checked={startEmpty} onChange={(e) => setStartEmpty(e.target.checked)} />
            Emménager dans un logement vide (le mobilier d'origine est retiré à ma première visite)
          </label>
        )}
        <div className="house-buy__actions">
          <button type="button" className="house-buy__button house-buy__button--secondary" onClick={close}>
            Non
          </button>
          {canRent && (
            <button type="button" className="house-buy__button house-buy__button--rent" onClick={() => confirm('rent')}>
              Louer
            </button>
          )}
          {canBuy && (
            <button type="button" className="house-buy__button" onClick={() => confirm('buy')}>
              {holdName ? 'Demander' : 'Acheter'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default HouseBuyPrompt;
